import { CHECKED_STATES, SelectionState } from '../constants/folderTree';
import { Operators, PiiQLSearchType } from '../constants/search';
import { AttributeFilter } from '../types/attributes';
import { Folder, FolderSelectionState } from '../types/tree';
import {
  extractSearchItemAttributeValue,
  isFilenameSearchPrefix,
  isSearchItemInlineFilter,
} from './fileSearchHelper';

/**
 * Functions to generator PiiQL filters from DAM search items
 */

export namespace PiiQLSearch {
  export type FilterItem = Pick<AttributeFilter, 'attribute' | 'options'> & {
    _searchType: PiiQLSearchType;
    _queryFieldPath: string;
  };

  export interface SearchItem {
    _attributeFilter?: FilterItem;
    _valueObj?: boolean | string | unknown;
    value?: string;
  }
}

/**
 * @param value - value of the range
 * @returns the PiiQL range value
 */
function rangeValue(value: unknown): unknown | '*' {
  // A null value means no lower/upper bound
  return value === null ? '*' : value;
}

function parseNumber(value: unknown): string | null {
  if (value === null) {
    return null;
  }
  if (typeof value === 'string') {
    value = Number.parseFloat(value);
  }
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }
  return value.toString();
}

/**
 * Creates a PiiQL filter to search for a filename
 *
 * @param namePrefix - the prefix to use when searching for a filename
 * @param filename - the filename to search for
 * @returns the PiiQL filter
 */
function nameFilter(namePrefix: string, filename: string): string {
  return [
    `(${namePrefix}:${filename}`, // Phrase includes word
    `${namePrefix}:'${filename}'`, // Value is exact word
    `${namePrefix}:*${filename}*)`, // Phrase contains string
  ].join(` ${Operators.Or} `);
}

/**
 * Maps a dam search item to one or several PiiQL filters
 *
 * @param item - the search item
 * @param namePrefix - the prefix to use when searching for a filename
 * @returns the PiiQL filter(s) for the search item
 */
function mapSearchItemToPiiQL(item: PiiQLSearch.SearchItem, namePrefix: string): string | string[] {
  if (item._attributeFilter) {
    const attrFilter = item._attributeFilter;
    const type = item._attributeFilter._searchType;

    switch (type) {
      case PiiQLSearchType.LINKS:
      case PiiQLSearchType.LINKS_ITEM_ID:
      case PiiQLSearchType.LINKS_LABEL:
        if (Array.isArray(item._valueObj) && item._valueObj.length) {
          // Search link by option itemId
          return item._valueObj.map(
            linkChoice => `${attrFilter._queryFieldPath}.ItemId:${linkChoice.itemId}`
          );
        } else if (typeof item.value === 'string') {
          const { value: attributeValue } = extractSearchItemAttributeValue(item.value);

          // Search link by option name
          return `${attrFilter._queryFieldPath}.Label.fr_FR:*${attributeValue}*`;
        }
        break;
      case PiiQLSearchType.BOOLEAN:
        if (
          (typeof item._valueObj === 'string' && item._valueObj.trim() === 'true') ||
          item._valueObj === true
        ) {
          return `${attrFilter._queryFieldPath}:true`;
        } else if (
          (typeof item._valueObj === 'string' && item._valueObj.trim() === 'false') ||
          item._valueObj === false
        ) {
          return `${attrFilter._queryFieldPath}:false`;
        }
        break;
      case PiiQLSearchType.EXACT_VALUE:
        if (Array.isArray(item._valueObj) && item._valueObj.length) {
          return item._valueObj.map(choice => `${attrFilter._queryFieldPath}:${choice.itemId}`);
        } else if (!Array.isArray(item._valueObj)) {
          return `${attrFilter._queryFieldPath}:${item._valueObj}`;
        }
        break;
      case PiiQLSearchType.NUMERIC:
        if (attrFilter.options.range && Array.isArray(item._valueObj)) {
          const start = rangeValue(parseNumber(item._valueObj[0]));
          const end = rangeValue(parseNumber(item._valueObj[1]));

          return `${attrFilter._queryFieldPath}:[${start} TO ${end}]`;
        }

        return `${attrFilter._queryFieldPath}:${item._valueObj}`;
      case PiiQLSearchType.DATE:
        if (attrFilter.options.range && Array.isArray(item._valueObj)) {
          let start = rangeValue(item._valueObj[0]);
          let end = rangeValue(item._valueObj[1]);

          // Date values need to be wrapped with single quotes
          start = start === '*' ? start : `'${start}'`;
          end = end === '*' ? end : `'${end}'`;

          return `${attrFilter._queryFieldPath}:[${start} TO ${end}]`;
        }

        return `${attrFilter._queryFieldPath}:*${item._valueObj}*`;
      case PiiQLSearchType.STRING:
      case PiiQLSearchType.LABEL:
        if (attrFilter.options.range && Array.isArray(item._valueObj)) {
          const start = rangeValue(item._valueObj[0]);
          const end = rangeValue(item._valueObj[1]);

          return `${attrFilter._queryFieldPath}:[${start} TO ${end}]`;
        }

        return `${attrFilter._queryFieldPath}:*${item._valueObj}*`;
      case PiiQLSearchType.NAME:
        if (item._valueObj && typeof item._valueObj === 'string') {
          return nameFilter(attrFilter._queryFieldPath, item._valueObj);
        }
        break;
    }
  } else if (isSearchItemInlineFilter(item.value)) {
    const { name: attributeName, value: attributeValue } = extractSearchItemAttributeValue(
      item.value
    );

    if (isFilenameSearchPrefix(attributeName)) {
      return nameFilter(namePrefix, attributeValue);
    }
  } else if (item.value) {
    return nameFilter(namePrefix, item.value);
  }

  return '';
}

/**
 * Transforms search items (from vue data) to abstract objets
 * to use for query generation
 *
 * @param searchGroups - groups of search items from the dam search. Each group will have
 * its own boolean operator between its items
 * @param opts - search options
 * @param opts.namePrefix - the prefix to use when searching for a filename
 * @param opts.join - the boolean operator to use to join search groups
 * @param opts.transform - transforms a search item before query generator
 * @returns an object of filters for a query generator
 */
export function generateFilePiiQLQuery(
  searchGroups: PiiQLSearch.SearchItem[][],
  opts: {
    namePrefix: string;
    join: Operators;
    transform?: (item: PiiQLSearch.SearchItem) => PiiQLSearch.SearchItem;
  }
): string {
  return searchGroups
    .map(group =>
      group
        .map(item => {
          opts.transform?.(item);
          return mapSearchItemToPiiQL(item, opts.namePrefix);
        })
        .filter(Boolean)
        .join(` ${Operators.And} `)
    )
    .filter(Boolean)
    .join(` ${opts.join} `);
}

/**
 * Generate query to apply filter on folders
 *
 * @param mode - mode to generate query : AND / OR ...
 * @param folders - the root folders (only those that have been loaded in the ui)
 * @param treeSelection - the current tree selection state
 */
export function generateQueryFolders(
  folders: Folder[],
  treeSelection: FolderSelectionState
): string {
  let query = '';

  for (const currentFolder of folders) {
    const selectionState = treeSelection[currentFolder.itemId];

    let folderQuery = '';

    switch (selectionState) {
      case SelectionState.MeOnly:
        folderQuery = `Paths:Any('${currentFolder.paths[0]}')`;
        break;
      case SelectionState.MeAndSomeChildren:
        folderQuery = `Paths:Any('${currentFolder.paths[0]}') ${
          Operators.Or
        } ${generateQueryFolders(currentFolder.children, treeSelection)}`;
        break;
      case SelectionState.NotMeButSomeChildren:
        folderQuery = generateQueryFolders(currentFolder.children, treeSelection);
        break;
      case SelectionState.NotMeButAllChildren:
        folderQuery = generateQueryFolders(currentFolder.children, treeSelection);
        break;
      case SelectionState.MeAndAllChildren:
        folderQuery = `Paths:Any(${currentFolder.paths[0]})`;
        break;
      case SelectionState.None:
      default:
        break;
    }
    if (folderQuery) {
      if (query.length) {
        query = `${query} ${Operators.Or} ${folderQuery}`;
      } else {
        query = folderQuery;
      }
    }
  }

  return query;
}
